import React from 'react';

// ── Direção 1 — "Secretaria" ────────────────────────────────────────────────
// Institucional e sóbrio, como um diário de classe: serifa nos títulos,
// tabelas pautadas, cantos quase retos, sem gradiente nem sombra.
export const D1 = {
  paper:     '#fbfaf7', // fundo geral (off-white quente)
  card:      '#ffffff', // cartões, tabelas, header
  ink:       '#1c2430', // texto principal, linha forte de cabeçalho de tabela
  ink2:      '#4a5563', // texto secundário
  ink3:      '#8a93a0', // rótulos, legendas, desabilitado
  line:      '#dcd8cf', // bordas de cartão e input
  line2:     '#ece9e2', // divisórias de linha de tabela
  acc:       '#1f3b6e', // azul institucional: faixa do topo, botão primário, item ativo, links
  accSoft:   '#e8edf5', // fundo do item ativo do menu
  ok:        '#1f7a4d', // presença, frequência >= 75%
  okSoft:    '#e3f1e9',
  alert:     '#b3261e', // falta, frequência < 75%, encerrar
  alertSoft: '#f8e4e2',
  riskRow:   '#fdf6f5', // fundo da linha de aluno em risco
} as const;

export const FONT = {
  serif: '"Source Serif 4", Georgia, serif',          // títulos e números grandes
  sans:  '"IBM Plex Sans", system-ui, sans-serif',     // interface e corpo
  mono:  '"IBM Plex Mono", ui-monospace, monospace',   // DRE, datas, PIN, percentuais
} as const;

// Regra de cor da frequência (obrigatória em todas as telas)
export const freqOk = (pct: number) => pct >= 75;
export const freqColor = (pct: number) => (pct >= 75 ? D1.ok : D1.alert);
export const fmtPin = (p: string) => (p && p.length === 6 ? p.slice(0, 3) + ' ' + p.slice(3) : p);

// Estilos-base reutilizáveis
export const d1: Record<string, React.CSSProperties> = {
  cap: { fontFamily: FONT.sans, fontSize: 11, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: D1.ink3 },
  h1:  { fontFamily: FONT.serif, fontSize: 34, fontWeight: 600, letterSpacing: -0.5, color: D1.ink, margin: 0, lineHeight: 1.15 },
  h2:  { fontFamily: FONT.serif, fontSize: 20, fontWeight: 600, color: D1.ink, margin: 0 },
  th:  { fontFamily: FONT.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', color: D1.ink3, textAlign: 'left', padding: '10px 12px', borderBottom: `1.5px solid ${D1.ink}`, background: D1.card },
  td:  { padding: '11px 12px', borderBottom: `1px solid ${D1.line2}`, fontSize: 13.5, color: D1.ink },
  btn: { fontFamily: FONT.sans, fontSize: 13, fontWeight: 600, padding: '10px 18px', background: D1.acc, color: '#fff', border: `1px solid ${D1.acc}`, borderRadius: 3, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, whiteSpace: 'nowrap', flexShrink: 0 },
  btnO:{ fontFamily: FONT.sans, fontSize: 13, fontWeight: 600, padding: '10px 18px', background: '#fff', color: D1.acc, border: `1px solid ${D1.acc}`, borderRadius: 3, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, whiteSpace: 'nowrap', flexShrink: 0 },
  btnR:{ fontFamily: FONT.sans, fontSize: 13, fontWeight: 600, padding: '10px 18px', background: D1.alert, color: '#fff', border: `1px solid ${D1.alert}`, borderRadius: 3, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, whiteSpace: 'nowrap', flexShrink: 0 },
  inp: { fontFamily: FONT.sans, fontSize: 14, padding: '10px 12px', border: `1px solid ${D1.line}`, borderRadius: 3, background: '#fff', color: D1.ink, width: '100%', boxSizing: 'border-box', outline: 'none' },
  card:{ background: D1.card, border: `1px solid ${D1.line}`, padding: '18px 20px' },
  link:{ fontFamily: FONT.sans, fontSize: 13, color: D1.acc, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  table:{ width: '100%', borderCollapse: 'collapse', background: D1.card, border: `1px solid ${D1.line}` },
};

// Selo de situação: REGULAR (verde) / EM RISCO (vermelho)
export const SitBadge = ({ pct, label }: { pct: number; label?: string }) => {
  const ok = freqOk(pct);
  return React.createElement('span', {
    style: { fontFamily: FONT.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: ok ? D1.ok : D1.alert, background: ok ? D1.okSoft : D1.alertSoft, padding: '3px 8px', borderRadius: 2, whiteSpace: 'nowrap' },
  }, label ?? (ok ? 'Regular' : 'Em risco'));
};

// Selo neutro (ex.: ENCERRADO, TRANCADA)
export const NeutralBadge = ({ children }: { children: React.ReactNode }) =>
  React.createElement('span', {
    style: { fontFamily: FONT.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: D1.ink2, background: D1.line2, padding: '3px 8px', borderRadius: 2, whiteSpace: 'nowrap' },
  }, children);

// ── Compatibilidade ──────────────────────────────────────────────────────────
// Os componentes antigos usam TOK.*; os nomes foram mantidos e apontam para a
// paleta nova, para que todas as telas herdem o visual sem mudar a lógica.
export const TOK = {
  teal: D1.acc, tealDark: '#152b52', tealSoft: D1.accSoft, tealMid: '#d3dceb', tealBar: D1.line2,
  navy: D1.ink, navySoft: D1.ink2, bg: D1.paper, card: D1.card, border: D1.line,
  ink: D1.ink, ink70: D1.ink2, ink50: '#6b7480', ink30: D1.ink3, ink10: D1.line2,
  ok: D1.ok, okSoft: D1.okSoft, alert: D1.alert, alertSoft: D1.alertSoft,
  heroGrad: D1.acc, // antes era gradiente; a Direção 1 não usa gradiente
  display: FONT.sans,
  serif: FONT.serif,
  sans: FONT.sans,
  mono: FONT.mono,
} as const;

// Estilos antigos (TS.*) — mesmos nomes, visual novo
export const TS: Record<string, React.CSSProperties> = {
  card:{ background:D1.card,border:`1px solid ${D1.line}`,borderRadius:3,padding:20,width:"100%",maxWidth:"100%",marginBottom:12,boxSizing:"border-box" },
  inp:{ width:"100%",padding:"10px 12px",borderRadius:3,border:`1px solid ${D1.line}`,fontSize:14,outline:"none",marginBottom:10,boxSizing:"border-box",background:"#fff",fontFamily:FONT.sans,color:D1.ink } as React.CSSProperties,
  btn:{ padding:"10px 18px",borderRadius:3,border:`1px solid ${D1.acc}`,background:D1.acc,color:"white",fontWeight:600,fontSize:13,cursor:"pointer",fontFamily:FONT.sans },
  btnG:{ padding:"10px 18px",borderRadius:3,border:`1px solid ${D1.ok}`,background:D1.ok,color:"white",fontWeight:600,fontSize:13,cursor:"pointer",marginRight:8,fontFamily:FONT.sans },
  btnR:{ padding:"10px 18px",borderRadius:3,border:`1px solid ${D1.alert}`,background:D1.alert,color:"white",fontWeight:600,fontSize:13,cursor:"pointer",fontFamily:FONT.sans },
  btnW:{ padding:"7px 12px",borderRadius:3,border:`1px solid ${D1.line}`,background:"white",color:D1.acc,fontWeight:600,fontSize:12,cursor:"pointer",marginRight:4,fontFamily:FONT.sans },
  btnY:{ padding:"7px 12px",borderRadius:3,border:"1px solid #e6d7a8",background:"#faf4e1",color:"#7a5a12",fontWeight:600,fontSize:12,cursor:"pointer",marginRight:4,fontFamily:FONT.sans },
  ok:{ background:D1.okSoft,color:D1.ok,borderRadius:3,padding:"10px 14px",fontSize:13,marginBottom:12 },
  err:{ background:D1.alertSoft,color:D1.alert,borderRadius:3,padding:"10px 14px",fontSize:13,marginBottom:12 },
};
